/**
 * Inspection Item Row Component
 * 
 * Single inspection item with severity selector, notes, and evidence
 */

import { useState, useCallback } from 'react';
import { ChevronDown, ChevronUp, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { SeveritySelector } from './SeveritySelector';
import { InspectionEvidenceUploader } from './InspectionEvidenceUploader';
import type { InspectionTemplateItem } from '@/hooks/useInspectionTemplate';
import type { InspectionItemResult, InspectionEvidence, InspectionSeverity, UpsertItemParams } from '@/hooks/useInspectionExecution';
import { cn } from '@/lib/utils';
import { useDebouncedCallback } from 'use-debounce';

interface InspectionItemRowProps {
  templateItem: InspectionTemplateItem;
  result: InspectionItemResult | undefined;
  evidence: InspectionEvidence[];
  shopId: string;
  ticketId: string;
  inspectionItemId: string | undefined;
  isEditable: boolean;
  onUpdate: (params: Omit<UpsertItemParams, 'templateItemId' | 'systemKey' | 'itemKey' | 'sharedKey'>) => Promise<void>;
  onEvidenceChange: () => void;
}

export function InspectionItemRow({
  templateItem,
  result,
  evidence,
  shopId,
  ticketId,
  inspectionItemId,
  isEditable,
  onUpdate,
  onEvidenceChange,
}: InspectionItemRowProps) {
  const [isNoteOpen, setIsNoteOpen] = useState(!!result?.note);
  const [localNote, setLocalNote] = useState(result?.note || '');

  const severity = result?.severity || null;
  const allowedSeverities = (templateItem.severity_policy?.allowed || ['PASS', 'CAUTION', 'FAIL', 'NA']) as InspectionSeverity[];

  // Check if evidence is required for current severity
  const evidenceRequirement = templateItem.evidence_requirements?.[severity || ''];
  const isEvidenceRequired = evidenceRequirement === 'REQUIRED';
  const hasEvidence = evidence.length > 0;
  const showEvidenceWarning = severity === 'FAIL' && isEvidenceRequired && !hasEvidence;

  // Debounced note update
  const debouncedNoteUpdate = useDebouncedCallback(
    (note: string) => {
      onUpdate({ note });
    },
    500
  );

  const handleSeverityChange = useCallback(async (newSeverity: InspectionSeverity) => {
    await onUpdate({ severity: newSeverity });
  }, [onUpdate]);

  const handleNoteChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newNote = e.target.value;
    setLocalNote(newNote);
    debouncedNoteUpdate(newNote);
  }, [debouncedNoteUpdate]);

  return (
    <div className={cn(
      'border rounded-lg p-3 space-y-3',
      showEvidenceWarning && 'border-destructive bg-destructive/5'
    )}>
      {/* Header: Item name and required badge */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="font-medium text-sm">{templateItem.name}</span>
            {templateItem.is_required && (
              <span className="text-xs px-1.5 py-0.5 bg-primary/10 text-primary rounded">
                Required
              </span>
            )}
          </div>
          {templateItem.description && (
            <p className="text-xs text-muted-foreground mt-0.5">
              {templateItem.description}
            </p>
          )}
        </div>
      </div>

      {/* Severity selector */}
      <SeveritySelector
        value={severity}
        onChange={handleSeverityChange}
        allowedSeverities={allowedSeverities}
        disabled={!isEditable}
        size="sm"
      />

      {/* Evidence warning */}
      {showEvidenceWarning && (
        <div className="flex items-center gap-2 text-destructive text-xs">
          <AlertCircle className="h-4 w-4" />
          <span>Evidence required for FAIL items</span>
        </div>
      )}

      {/* Evidence uploader */}
      {inspectionItemId && (
        <InspectionEvidenceUploader
          shopId={shopId}
          ticketId={ticketId}
          inspectionItemId={inspectionItemId}
          itemKey={templateItem.item_key}
          sharedKey={templateItem.shared_key}
          evidence={evidence}
          disabled={!isEditable}
          onUploadComplete={onEvidenceChange}
        />
      )}

      {/* Collapsible notes section */}
      <Collapsible open={isNoteOpen} onOpenChange={setIsNoteOpen}>
        <CollapsibleTrigger asChild>
          <Button variant="ghost" size="sm" className="gap-1 h-7 text-xs">
            {isNoteOpen ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            {result?.note ? 'Edit Note' : 'Add Note'}
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent className="pt-2">
          <Textarea
            placeholder="Add notes about this inspection item..."
            value={localNote}
            onChange={handleNoteChange}
            disabled={!isEditable}
            rows={2}
            className="text-sm"
          />
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}
